import { dateRangeFilterOptions, months } from "../consts";

const ONE_DAY_IN_MILLISECONDS = 86_400_000;

const calculateRange = (filterOption) => {
  let fromDate;
  let toDate;

  const today = new Date();

  switch (filterOption) {
    case dateRangeFilterOptions.TODAY: {
      fromDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0);
      toDate = today;
      break;
    }

    case dateRangeFilterOptions.LAST_24_HOURS: {
      fromDate = new Date(new Date().setDate(today.getDate() - 1));
      toDate = today;
      break;
    }

    case dateRangeFilterOptions.THIS_WEEK: {
      fromDate = new Date(getSunday(today).setHours(0, 0, 0));
      toDate = today;
      break;
    }

    case dateRangeFilterOptions.LAST_7_DAYS: {
      fromDate = new Date(new Date().setDate(today.getDate() - 7));
      toDate = today;
      break;
    }

    case dateRangeFilterOptions.THIS_MONTH: {
      fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
      toDate = today;
      break;
    }

    case dateRangeFilterOptions.LAST_30_DAYS: {
      fromDate = new Date(new Date().setDate(today.getDate() - 30));
      toDate = today;
      break;
    }

    case dateRangeFilterOptions.THIS_YEAR: {
      fromDate = new Date(today.getFullYear(), 0, 1);
      toDate = today;
      break;
    }

    case dateRangeFilterOptions.LAST_12_MONTHS: {
      fromDate = new Date(new Date().setDate(today.getDate() - 365));
      toDate = today;
      break;
    }
  }

  return { fromDate, toDate };
};

const getSunday = (d) => {
  d = new Date(d);
  const day = d.getDay();
  const diff = d.getDate() - day + (day == 0 ? -6 : 0);
  return new Date(d.setDate(diff));
};

const getDateFromTimestamp = (timestamp) => {
  if (timestamp) {
    const date = new Date(Number.parseInt(timestamp));

    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();

    return new Date(year, month, day);
  }
};

const formatDateFromTimestamp = (timestamp, includeTime = true) => {
  if (timestamp) {
    const date = new Date(Number.parseInt(timestamp));

    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();

    const hours = date.getHours();
    const minutes = date.getMinutes();

    let result = `${addLeadingZero(day)}/${addLeadingZero(month + 1)}/${year}`;

    if (includeTime) {
      result += `  ${addLeadingZero(hours)}:${addLeadingZero(minutes)}`;
    }

    return result;
  }
};

const getMinDate = (dates) => {
  return dates.reduce(function (a, b) {
    return a < b ? a : b;
  });
};

const addLeadingZero = (value) => {
  return ("0" + value).slice(-2);
};

const formatHour = (date, step = 2) => {
  if (date) {
    const day = date.getDate();
    const hours = date.getHours();

    return `${addLeadingZero(day)}-${addLeadingZero(hours - (hours % step))}:00`;
  }
};

const formatDay = (date) => {
  if (date) {
    const day = date.getDate();
    const month = date.getMonth();

    return `${addLeadingZero(day)}. ${months.find((m) => m.id === month)?.month}`;
  }
};

const formatMonthName = (date) => {
  if (date) {
    const month = date.getMonth();
    const year = date.getFullYear();

    return `${months.find((m) => m.id === month)?.month} ${year.toString().slice(-2)}`;
  }
};

const formatMonthDay = (date) => {
  if (date) {
    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();

    return `${year}-${addLeadingZero(month + 1)}-${addLeadingZero(day)}`;
  }
};

Date.prototype.addHours = function (hours) {
  const date = new Date(this.valueOf());
  date.setHours(date.getHours() + hours);
  return date;
};

Date.prototype.addDays = function (days) {
  const date = new Date(this.valueOf());
  date.setDate(date.getDate() + days);
  return date;
};

Date.prototype.addMonths = function (months) {
  const date = new Date(this.valueOf());
  date.setDate(1);
  date.setMonth(date.getMonth() + months);
  return date;
};

const getHoursList = (startDate, endDate, step = 2) => {
  const datesList = [];

  let currentDate = startDate;
  while (currentDate <= endDate) {
    datesList.push(new Date(currentDate));
    currentDate = currentDate.addHours(step);
  }

  return datesList;
};

const getDatesList = (startDate, endDate) => {
  const datesList = [];

  let currentDate = startDate;
  while (currentDate <= endDate) {
    datesList.push(new Date(currentDate));
    currentDate = currentDate.addDays(1);
  }

  return datesList;
};

const getMonthsList = (startDate, endDate) => {
  const datesList = [];

  let currentDate = startDate;
  while (currentDate <= endDate) {
    datesList.push(new Date(currentDate));
    currentDate = currentDate.addMonths(1);
  }

  return datesList;
};

const getFormattedDatesList = (period, dateListGetter, formatFn, range) => {
  let fromDate;
  let toDate;

  const calculatedRange = calculateRange(period);

  if (calculatedRange?.fromDate && calculatedRange?.toDate) {
    fromDate = calculatedRange.fromDate;
    toDate = calculatedRange.toDate;
  } else if (range && range?.from && range?.to) {
    fromDate = range.from;
    toDate = range.to;
  } else {
    return [];
  }

  const datesList = dateUtil[dateListGetter](fromDate, toDate, 2);

  return datesList.map((date) => dateUtil[formatFn](date));
};

const getDaysDiff = (startDate, endDate) => {
  const difference = endDate.getTime() - startDate.getTime();
  const days = Math.ceil(difference / (1000 * 3600 * 24));

  return days;
};

const isToday = (date) => {
  const today = new Date();

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const isYesterday = (date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
};

const convertToUTCTimestamp = (date, endOfTheDay = false) => {
  if (date) {
    const [day, month, year] = date.split("/").map(Number);

    const localDate = new Date(year, month - 1, day);

    const offset = localDate.getTimezoneOffset();

    const timestamp = localDate.getTime() - (offset < 0 ? offset : offset * -1) * 60 * 1000;

    return endOfTheDay ? timestamp + ONE_DAY_IN_MILLISECONDS - 1000 : timestamp;
  }
};

const getMonthName = (date) => {
  if (date) {
    const month = date.getMonth();

    return months.find((m) => m.id === month)?.month;
  }
};

const isCurrentYear = (date) => {
  const today = new Date();

  return date.getFullYear() === today.getFullYear();
};

const formatDateAndTime = (date, timeDate) => {
  const day = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();

  const hours = timeDate.getHours();
  const minutes = timeDate.getMinutes();

  return `${addLeadingZero(day)}-${addLeadingZero(month + 1)}-${year} ${hours}:${addLeadingZero(
    minutes
  )}`;
};

const getUTCDate = (date) => {
  const d = date ? date : new Date();
  return new Date(d.getTime() + d.getTimezoneOffset() * 60000);
};

const combineDateTime = (baseDate, timeDate) => {
  const dateTime = new Date(baseDate);

  dateTime.setHours(timeDate.getHours());
  dateTime.setMinutes(timeDate.getMinutes());

  return dateTime;
};

const addMinutes = (date, minutes) => {
  return new Date(date.getTime() + minutes * 60000);
};

const toDateStringNoTimezone = (date) => {
  const day = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();

  const hours = date.getHours();
  const minutes = date.getMinutes();

  return `${year}-${addLeadingZero(month + 1)}-${addLeadingZero(day)}T${addLeadingZero(
    hours
  )}:${addLeadingZero(minutes)}:00.000Z`;
};

const toTimestampNoTimezone = (date) => {
  if (!date) return null;

  const utc = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  );

  return utc;
};

const getDateDifferenceInMilliseconds = (firstDate, secondDate) => {
  return new Date(firstDate).getTime() - new Date(secondDate).getTime();
};

const isSameDate = (firstDate, secondDate) => {
  return new Date(firstDate).toDateString() === new Date(secondDate).toDateString();
};

// Format like DD/MM/YYYY HH:mm:ss
const formatDateFromDayFirstFormat = (date) => {
  const [day, month, year, time] = date.split(/[/\s]/);
  const [hours, minutes, seconds] = time.split(":");

  return new Date(year, month - 1, day, hours, minutes, seconds);
};

const dateUtil = {
  addLeadingZero,
  calculateRange,
  getDateFromTimestamp,
  formatDateFromTimestamp,
  getMinDate,
  formatHour,
  formatDay,
  formatMonthDay,
  formatMonthName,
  getHoursList,
  getDatesList,
  getMonthsList,
  getFormattedDatesList,
  getDaysDiff,
  isToday,
  isYesterday,
  convertToUTCTimestamp,
  getMonthName,
  isCurrentYear,
  formatDateAndTime,
  getUTCDate,
  combineDateTime,
  addMinutes,
  toDateStringNoTimezone,
  getDateDifferenceInMilliseconds,
  isSameDate,
  formatDateFromDayFirstFormat,
  toTimestampNoTimezone
};

export default dateUtil;
